"use client";

import React, { useMemo } from "react";
import dynamic from "next/dynamic";

import NavBarV2 from "@/components/global/partials/HeaderV2";
import type { PageFabioAffiliateSchema } from "@/sanity/pages/PageFabioAffiliateTemplate";

import HeroSection from "../pageComponents/fabioAffiliate/HeroSection";
import VipDealSection from "../pageComponents/fabioAffiliate/VipDealSection";
import IntroSection from "../pageComponents/fabioAffiliate/IntroSection";
import DealsSection from "../pageComponents/fabioAffiliate/DealsSection";
import StepsSection from "../pageComponents/fabioAffiliate/StepsSection";

const TradersSaySection = dynamic(() => import("../pageComponents/fabioAffiliate/TradersSaySection"));
const ComparisonSection = dynamic(() => import("../pageComponents/fabioAffiliate/ComparisonSection"));
const TestimonialsSection = dynamic(() => import("../pageComponents/fabioAffiliate/TestimonialsSection"));
const FAQSection = dynamic(() => import("../pageComponents/fabioAffiliate/FAQSection"));
const FinalCTASection = dynamic(() => import("../pageComponents/fabioAffiliate/FinalCta"));
const FooterFabio = dynamic(() => import("../global/partials/FooterFabioPage"));

export default function PageFabioAffiliateJSX({ data }: { data: PageFabioAffiliateSchema }) {
  const marqueeItems = useMemo(() => {
    if (!data?.heroPills?.length) return null;
    return [0, 1].flatMap((copyIndex) =>
      data.heroPills!.map((item, index) => (
        <div
          key={`${copyIndex}-${index}`}
          className="flex flex-shrink-0 items-center"
          aria-hidden={copyIndex === 1 ? true : undefined}>
          <p className="m-0 font-mono leading-none uppercase whitespace-nowrap text-black/60 font-[500] tracking-[0.04rem]">
            {item.text}
          </p>
          <div className="flex-shrink-0 w-[.5rem] h-[.5rem] bg-[#228E71] mx-[5rem]" />
        </div>
      )),
    );
  }, [data?.heroPills]);

  return (
    <main className="bg-white">
      {data?.settings?.header && <NavBarV2 headerData={data.settings.header} />}

      <HeroSection data={data} />

      <VipDealSection data={data} />

      <IntroSection data={data} />

      <DealsSection data={data} />

      <div className="flex overflow-hidden items-center w-full z-[9] py-[3.5rem] border-y">
        {marqueeItems && (
          <div
            className="flex flex-shrink-0 items-center"
            style={{
              animation: `scrollLeft ${data!.heroPills!.length * 20}s linear infinite`,
              willChange: "transform",
              transform: "translateZ(0)",
            }}>
            {marqueeItems}
          </div>
        )}
      </div>

      <StepsSection data={data} />

      {(data?.tradersSayChip || data?.tradersSayHeading || data?.tradersSayItems?.length || data?.tradersSayCTA) && (
        <TradersSaySection data={data} />
      )}

      <ComparisonSection data={data} />

      {(data?.testimonialsChip ||
        data?.testimonialsHeading ||
        data?.testimonialsRating ||
        data?.testimonialsRatingText ||
        data?.testimonialsItems?.length) && <TestimonialsSection data={data} />}

      {(data?.faqChip || data?.faqHeading || data?.faqItems?.length) && <FAQSection data={data} />}

      {(data?.finalCtaChip ||
        data?.finalCtaHeading ||
        data?.finalCtaSubheading ||
        data?.finalCtaButton ||
        data?.finalCtaImage) && <FinalCTASection data={data} />}

      {data?.settings?.footer && (
        <FooterFabio
          data={data.settings.footer}
          slug={data?.settings?.slug?.current}
        />
      )}
    </main>
  );
}
